import { Fragment } from 'react'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import * as _ from 'lodash-es'

dayjs.extend(utc)

export const tick_wrapper = (formatter: (value: number) => string) => {
  return (value: unknown) => formatter(value as number)
}

export const usdFormatter = (value?: number | string, showCents = true) => {
  return usdFormatterV2(value, { showCents })
}

// eslint-disable-next-line complexity
export const usdFormatterV2 = (
  value?: number | string,
  {
    showCents = true,
    maximumFractionDigits = 2,
  }: { showCents?: boolean; maximumFractionDigits?: number } = {},
) => {
  value = Number(value ?? 0)

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: showCents ? maximumFractionDigits : 0,
    minimumFractionDigits: showCents ? 2 : 0,
  })
  const text = formatter.format(value)
  // fix -0 issue
  if (showCents) {
    return text == '-$0.00' ? '$0.00' : text
  }
  return text == '-$0' ? '$0' : text
}

export const getMaxFractionDigits = (symbol: string) =>
  symbol === 'USDC' ? 2 : 4

export const usdAutoFormatter = (value?: number | string, showCents = true) => {
  value = Number(value ?? 0)

  if (value < 0.02) {
    return usdFormatterV2(value, {
      showCents,
      maximumFractionDigits: 4,
    })
  }
  if (value < 100_000) {
    return usdFormatter(value, showCents)
  }
  return usdLargeNumberFormatter(value, { maximumFractionDigits: 2 })
}

export const usdAutoFormatterNoCents = (value?: number | string) => {
  return usdAutoFormatter(value, false)
}

export const countFormatter = (
  value: number | string,
  {
    compact = true,
    maximumFractionDigits = 1,
  }: {
    compact?: boolean
    maximumFractionDigits?: number
  } = {},
) => {
  value = Number(value)

  const formatter = new Intl.NumberFormat('en-US', {
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits,
  })
  return formatter.format(value)
}

export const countAutoFormatter = (value?: number | string) => {
  value = Number(value ?? 0)

  const formatter = new Intl.NumberFormat('en-US', {
    notation: value < 1_000 ? 'standard' : 'compact',
    maximumFractionDigits: value < 1_000 ? 4 : 2,
  })
  return formatter.format(value)
}

export function possiblyUnlimited(value: number, formatted_value: string) {
  if (value > 1e14) {
    return 'unlimited'
  }
  return formatted_value
}

// eslint-disable-next-line complexity
export const usdLargeNumberFormatter = (
  value?: number | string | null,
  {
    missing_make_blank = false,
    maximumFractionDigits = 0,
    missing_string = '',
  }: {
    missing_make_blank?: boolean
    maximumFractionDigits?: number
    missing_string?: string
  } = {},
) => {
  if (value === null || (missing_make_blank && value == null)) {
    return missing_string
  }
  value = Number(value ?? 0)

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits,
    notation: 'compact',
  })

  return formatter.format(value)
}

export const value_formatter = (value: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: 2,
    notation: 'compact',
  })
  return formatter.format(value)
}

export const crypto_value_formatter = (
  value: number,
  {
    maximumFractionDigits = 0,
  }: {
    maximumFractionDigits?: number
  } = {},
) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    notation: 'compact',
    maximumFractionDigits,
  })
  return formatter.format(value)
}

// eslint-disable-next-line complexity
export const percentage_formatter = (
  percent: number,
  {
    compact = true,
    maximumFractionDigits = 2,
    minimumFractionDigits,
    add_plus = false,
  }: {
    compact?: boolean
    maximumFractionDigits?: number
    minimumFractionDigits?: number
    add_plus?: boolean
  } = {},
) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits,
    minimumFractionDigits,
  })
  const percentage = formatter.format(percent / 100)
  if (add_plus && percent > 0) {
    return `+${percentage}`
  }

  return percentage == '-0%' ? '0%' : percentage
}

export const percentage_range_formatter = (
  percent_lower: number,
  percent_upper: number,
  options: {
    compact?: boolean
    maximumFractionDigits?: number
    minimumFractionDigits?: number
  } = {},
  // eslint-disable-next-line max-params
) => {
  const percentage_low = _.trimEnd(
    percentage_formatter(percent_lower, options),
    '%',
  )
  const percentage_high = percentage_formatter(percent_upper, options)

  return `${percentage_low} - ${percentage_high}`
}

const DATE_FORMAT = "MMM 'YY"
export const date_formatter = (value: number) =>
  dayjs.utc(value).format(DATE_FORMAT)

export const date_s_formatter = (value: number) =>
  dayjs.unix(value).format(DATE_FORMAT)

export const full_date_formatter = (value?: dayjs.ConfigType) =>
  dayjs(value).format('MMM DD, YYYY')

export const date_month_date_formatter = (value?: dayjs.ConfigType) =>
  dayjs(value).format('MMM D')

export const legend_name_formatter = (name: string) => _.startCase(name)

export const hour_formatter = (value: number) => {
  const time = dayjs(value)
  if (time.hour() === 0) {
    return time.format(DATE_FORMAT)
  }
  return time.format('h:mm a')
}

export const local_date_time_formatter = (value?: dayjs.ConfigType) =>
  dayjs(value).toDate().toLocaleString()

export const empty_to_na_formatter = (
  ...args: Parameters<typeof empty_to_none_formatter>
) =>
  empty_to_none_formatter(args[0], args[1], {
    ...args[2],
    missing_string: 'N/A',
  })

export const empty_to_none_formatter = <
  FOptions extends {
    missing_string?: string
  },
>(
  value: number | null | undefined,
  formatter: (v: number, options?: Omit<FOptions, 'missing_string'>) => string,
  options?: FOptions,
  // eslint-disable-next-line max-params
) => {
  const missing_string = options?.missing_string ?? 'None'
  if (value == 0 || value == null) return missing_string
  return formatter(value, options)
}

export const human_days_formatter = (days: number) => {
  if (days == 0) return 'None'
  return `${days} days`
}

export const human_months_frequency_formatter = (months: number) => {
  if (months == 3) return 'Quarterly'
  if (months == 1) return 'Monthly'
  return `${months} Months`
}

export const convert_space_to_nbsp = (text: string, count: number) => {
  const parts = text.split(' ')

  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {part}
          {index < count ? <>&nbsp;</> : ' '}
        </Fragment>
      ))}
    </>
  )
}

export const shorted_address = (address: string | undefined) => {
  if (!address) return ''
  return `${address.slice(0, 5)}...${address.slice(-4)}`
}
